import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Select, SelectOptionGroup } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { Template } from '../../hooks/useTemplates';
import { MARKET_LANGUAGE } from '../../lib/markets';
import { Clock, Layers, LayoutList, Group } from 'lucide-react';

const FORMAT_LABELS: Record<string, string> = {
  shorts_featured_video: 'Shorts Featured Video',
  full_screen_interstitial: 'Full Screen Interstitial',
};

export interface TemplateSelectorProps {
  templates: Template[];
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string) => void;
  disabled?: boolean;
  /** The currently selected market code (e.g. "global", "jp"). Used to surface matching templates first within each group. */
  selectedMarket?: string;
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplateId,
  onSelectTemplate,
  disabled = false,
  selectedMarket,
}) => {
  const [grouped, setGrouped] = useState(false);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  /** Sort templates so those matching the active market language appear first. */
  const sorted = [...templates].sort((a, b) => {
    const marketLang = selectedMarket ? (MARKET_LANGUAGE[selectedMarket] ?? null) : null;
    if (!marketLang) return 0;
    const aMatch = a.language === marketLang ? 0 : 1;
    const bMatch = b.language === marketLang ? 0 : 1;
    return aMatch - bMatch;
  });

  const toOption = (t: Template) => ({
    value: t.id,
    label: t.name,
  });

  const selectElement = grouped ? (() => {
    const formats = Array.from(new Set(sorted.map((t) => t.format)));
    const groups: SelectOptionGroup[] = formats.map((fmt) => ({
      label: FORMAT_LABELS[fmt] ?? fmt,
      options: sorted.filter((t) => t.format === fmt).map(toOption),
    }));
    return (
      <Select
        value={selectedTemplateId || ''}
        onChange={onSelectTemplate}
        groups={groups}
        placeholder="Choose a template"
        disabled={disabled}
        className="mb-4"
      />
    );
  })() : (
    <Select
      value={selectedTemplateId || ''}
      onChange={onSelectTemplate}
      options={sorted.map(toOption)}
      placeholder="Choose a template"
      disabled={disabled}
      className="mb-4"
    />
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Template</CardTitle>
        {/* Group-by toggle */}
        <button
          type="button"
          onClick={() => setGrouped((g) => !g)}
          title={grouped ? 'Show flat list' : 'Group by format'}
          className="ml-auto flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary transition-colors"
        >
          {grouped ? (
            <><LayoutList className="w-3.5 h-3.5" />Flat</>
          ) : (
            <><Group className="w-3.5 h-3.5" />Group</>
          )}
        </button>
      </CardHeader>
      <CardContent>
        {selectElement}

        {selectedTemplate && (
          <div className="mt-4 p-4 bg-bg-tertiary border border-border-subtle rounded-md space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-text-primary">
                {selectedTemplate.name}
              </h4>
              <Badge variant="success">v{selectedTemplate.version}</Badge>
            </div>

            <div className="flex items-center gap-4 text-xs text-text-secondary">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>{selectedTemplate.duration}s</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5" />
                <span>{selectedTemplate.slots.filter((slot) => slot.visible !== false).length} slots</span>
              </div>
            </div>

            <div className="pt-2 border-t border-border-subtle">
              <p className="text-xs font-medium text-text-tertiary mb-2">Required Assets:</p>
              <div className="space-y-1">
                {selectedTemplate.slots.filter((slot) => slot.visible !== false).map((slot) => (
                  <div key={slot.slotId} className="flex items-center gap-2 text-xs">
                    <Badge size="sm" variant="default">
                      {slot.type}
                    </Badge>
                    <span className="text-text-secondary">{slot.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
